// App.jsx (React, no TypeScript) — keep WS open after commit; render AI text lines
import React, { useEffect, useRef, useState } from 'react';

export default function App() {
  const [topic, setTopic] = useState('');
  const [logs, setLogs] = useState([]);
  const [aiLines, setAiLines] = useState([]);

  const wsRef = useRef(null);
  const audioCtxRef = useRef(null);
  const procRef = useRef(null);
  const streamRef = useRef(null);
  const silenceTimerRef = useRef(null);
  const speakingRef = useRef(false);

  function logLine(s) {
    setLogs((prev) => [...prev, s]);
  }
  function addAI(line) {
    setAiLines((prev) => [...prev, line]);
  }

  // Float32 (-1..1) -> Int16 PCM
  function floatTo16PCM(input) {
    const out = new Int16Array(input.length);
    for (let i = 0; i < input.length; i++) {
      let s = Math.max(-1, Math.min(1, input[i]));
      out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }
    return out.buffer;
  }

  async function startTalking() {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      logLine('ws already open');
      return;
    }

    const url =
      (location.protocol === 'https:' ? 'wss://' : 'ws://') + location.host + '/';
    const ws = new WebSocket(url);
    ws.binaryType = 'arraybuffer';
    wsRef.current = ws;

    ws.onopen = () => {
      logLine('ws: open');
      if (topic.trim()) {
        ws.send(JSON.stringify({ type: 'meta', text: topic.trim() }));
      }
      setupMic();
    };

    ws.onclose = () => {
      logLine('ws closed');
      cleanupMic();
      wsRef.current = null;
    };

    ws.onerror = (e) => {
      logLine('ws error: ' + (e && e.message ? e.message : String(e)));
      cleanupMic();
    };

    ws.onmessage = (ev) => {
      if (typeof ev.data === 'string') {
        try {
          const msg = JSON.parse(ev.data);
          if (msg.type === 'log' && msg.text) logLine('log: ' + msg.text);
          if (msg.type === 'ai' && msg.line) addAI(msg.line);
        } catch {
          /* ignore */
        }
      } else {
        // binary audio chunk (24k PCM16) — 目前先忽略，不要關 WS
      }
    };
  }

  async function setupMic() {
    if (speakingRef.current) return;
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const audioCtx = new AudioCtx({ sampleRate: 24000 });
      audioCtxRef.current = audioCtx;

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const src = audioCtx.createMediaStreamSource(stream);
      const proc = audioCtx.createScriptProcessor(2048, 1, 1);
      procRef.current = proc;

      const SILENCE_MS = 1200; // 無聲 1.2 秒就送 commit
      const SILENCE_THR = 0.005;

      proc.onaudioprocess = (e) => {
        const ch = e.inputBuffer.getChannelData(0);

        // 送 PCM 給後端
        const ws = wsRef.current;
        if (ws && ws.readyState === WebSocket.OPEN) {
          ws.send(floatTo16PCM(ch));
        }

        // 簡單靜音偵測
        let max = 0;
        for (let i = 0; i < ch.length; i++) {
          const v = Math.abs(ch[i]);
          if (v > max) max = v;
        }

        if (max < SILENCE_THR) {
          if (!silenceTimerRef.current) {
            silenceTimerRef.current = setTimeout(() => {
              commitTurn();
            }, SILENCE_MS);
          }
        } else {
          if (silenceTimerRef.current) {
            clearTimeout(silenceTimerRef.current);
            silenceTimerRef.current = null;
          }
        }
      };

      src.connect(proc);
      proc.connect(audioCtx.destination);
      speakingRef.current = true;
      logLine('mic ok');
    } catch (e) {
      logLine('mic error: ' + (e && e.message ? e.message : String(e)));
    }
  }

  function cleanupMic() {
    speakingRef.current = false;
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    try { procRef.current && procRef.current.disconnect(); } catch {}
    try { streamRef.current && streamRef.current.getTracks().forEach((t) => t.stop()); } catch {}
    try { audioCtxRef.current && audioCtxRef.current.close(); } catch {}
    procRef.current = null;
    streamRef.current = null;
    audioCtxRef.current = null;
  }

  function commitTurn() {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify({ type: 'commit' }));
    logLine('commit (silence)');
    // 不關 ws，等待 AI 文字回覆
  }

  useEffect(() => {
    return () => {
      try { wsRef.current && wsRef.current.close(); } catch {}
      cleanupMic();
    };
  }, []);

  return (
    <div style={{ fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto' }}>
      <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
        <input
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          placeholder="Topic (optional)"
          style={{ flex: 1, padding: 8, borderRadius: 8, border: '1px solid #ddd' }}
        />
        <button
          onClick={startTalking}
          style={{
            padding: '8px 12px',
            borderRadius: 8,
            border: 0,
            background: '#f2633f',
            color: '#fff',
            cursor: 'pointer',
          }}
        >
          Start Talking
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16 }}>
        <pre
          style={{
            background: '#111',
            color: '#0f0',
            padding: 8,
            minHeight: 200,
            borderRadius: 8,
            overflow: 'auto',
          }}
        >
          {logs.join('\n')}
        </pre>

        <div
          style={{
            border: '1px solid #eee',
            borderRadius: 8,
            padding: 8,
            minHeight: 200,
            overflow: 'auto',
          }}
        >
          {aiLines.length === 0 ? (
            <div style={{ color: '#999' }}>AI replies will appear here…</div>
          ) : (
            aiLines.map((line, i) => <div key={i}>{line}</div>)
          )}
        </div>
      </div>
    </div>
  );
}
